import { useMemo } from 'react';
import { Space, Table } from 'antd';
import { Answers } from './OhatResultGraph';
import { Question } from '../../../constants/question';
import { RESULT_COLORS } from '../../../constants/result-colors';
import { renderStatusCell, statusCellProps } from './resultTableCell';
import {
  getScoreTableRow,
  getTotalScore,
} from '../../../utils/ohat-score-utils';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

// 見出しも印刷でつぶれないよう、太字にせず大きめの文字で表示する。
// 英語の長い単語でも列幅を超えないよう、単語の途中で折り返せるようにする。
const renderTitle = (q: Question, lang?: string) => {
  return (
    <Space.Compact orientation="vertical">
      <span
        style={{ fontSize: '13px', fontWeight: 400, overflowWrap: 'anywhere' }}
      >
        {q.key}
      </span>
      <span
        style={{ fontSize: '14px', fontWeight: 400, overflowWrap: 'anywhere' }}
      >
        {q.getLabel(lang)}
      </span>
    </Space.Compact>
  );
};

// ラベルの色を取得する。「今回」はオレンジ（レーダーチャートと同色）、「前回」「初回」はグレー。
const getLabelColor = (label: string): string | undefined => {
  // 翻訳後のテキストに対応するため、labelフィールドの先頭部分で判定する。
  // data生成時に `thisTime` / `previous` / `first` のキーに基づいてラベルが設定される。
  if (label === 'thisTime') return RESULT_COLORS.thisTime;
  if (label === 'previous') return RESULT_COLORS.previous;
  if (label === 'first') return RESULT_COLORS.firstLabel;
  return undefined;
};

// ラベルセル内に合計スコア・評価者名を併記する。tを使うためファクトリにしている。
// 太字・極細は印刷でつぶれる／かすれるため、ウェイトは400で統一し色だけで区別する。
const makeRenderLabelColumn =
  (t: TFunction) => (text: string, record: any) => {
    const color = record?.colorKey ? getLabelColor(record.colorKey) : undefined;
    const isThisTime = record?.colorKey === 'thisTime';
    // 「今回」等（日付なし）は '.' を含まない。前回/初回は `ラベル.日付`。
    const hasDate = text.includes('.');
    const [label, date] = hasDate ? text.split('.') : [text, undefined];
    return (
      <Space.Compact orientation="vertical">
        <span
          style={{
            fontWeight: 400,
            fontSize: '15px',
            color,
            overflowWrap: 'anywhere',
          }}
        >
          {label}
        </span>
        {date && (
          <span
            style={{
              fontWeight: 400,
              fontSize: '12px',
              overflowWrap: 'anywhere',
            }}
          >
            {date}
          </span>
        )}
        {typeof record?.total === 'number' && (
          <span
            style={{
              fontWeight: 400,
              fontSize: '14px',
              color: isThisTime ? RESULT_COLORS.thisTime : '#595959',
              overflowWrap: 'anywhere',
            }}
          >
            {/* nowrap だと列幅を押し広げるため使わない。「合計」と点数が
                別の行に分かれないよう、間だけ NBSP でつなぐ。 */}
            {`${t('ohat.total')}\u00a0${record.total}pt`}
          </span>
        )}
        {record?.evaluatorName && (
          <span
            style={{
              fontWeight: 400,
              fontSize: '12px',
              color: '#8c8c8c',
              overflowWrap: 'anywhere',
            }}
          >
            {record.evaluatorName}
          </span>
        )}
      </Space.Compact>
    );
  };

// tableLayout="fixed" で列幅を固定し、表が親幅を超えないようにする。
// ラベル列（15%）は日付「2026/09/19」や「Total 10pt」「Previous」が1行に収まる幅にし、
// 残りを Q1〜Q8 で均等に割る。ラベル列がない（今回のみ）ときは8列で均等割り。
const LABEL_COLUMN_PERCENT = 15;
const QUESTION_COUNT = 8;

type Props = {
  answers: Answers;
  oldAnswers: Answers[];
};
export default function OhatResultTable(props: Props) {
  const { answers, oldAnswers } = props;
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const data = useMemo(() => {
    const [first, latest] = oldAnswers;
    return [
      Object.assign(
        {
          label: t('ohat.thisTime'),
          colorKey: 'thisTime',
          total: getTotalScore(answers),
          evaluatorName: answers.evaluatorName,
        },
        getScoreTableRow('thisScore', answers),
      ),
      latest &&
        Object.assign(
          {
            label: `${t('ohat.previous')}.${latest.evaluatedAt}`,
            colorKey: 'previous',
            total: getTotalScore(latest),
            evaluatorName: latest.evaluatorName,
          },
          getScoreTableRow('latestScore', latest),
        ),
      first &&
        Object.assign(
          {
            label: `${t('ohat.first')}.${first.evaluatedAt}`,
            colorKey: 'first',
            total: getTotalScore(first),
            evaluatorName: first.evaluatorName,
          },
          getScoreTableRow('firstScore', first),
        ),
    ].filter(Boolean); //nullを除去
  }, [answers, oldAnswers, t]);

  const hasLabelColumn = data.length > 1;
  const dataColumnWidth = `${
    (100 - (hasLabelColumn ? LABEL_COLUMN_PERCENT : 0)) / QUESTION_COUNT
  }%`;
  const dataColumns = Question.getAll().map((q, i) => {
    return {
      title: renderTitle(q, lang),
      dataIndex: i,
      key: q.key,
      width: dataColumnWidth,
      align: 'center' as const,
      // 背景色はセル全体に敷くため onCell で指定する。
      onCell: statusCellProps(i),
      render: renderStatusCell(lang),
    };
  });

  return (
    <Table
      dataSource={data}
      columns={
        !hasLabelColumn
          ? dataColumns
          : [
              {
                title: '',
                dataIndex: 'label',
                key: 'label',
                width: `${LABEL_COLUMN_PERCENT}%`,
                align: 'center',
                rowScope: 'row',
                render: makeRenderLabelColumn(t),
              },
              ...dataColumns,
            ]
      }
      tableLayout="fixed"
      bordered
      pagination={false}
      className={'ant-slim-table'}
      size="small"
    />
  );
}
